<template>
  <div ref="containerRef" :class="`animated-content ${className}`">
    <slot />
  </div>
</template>

<script>
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// 注册 GSAP 插件
gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AnimatedContent',
  props: {
    // 动画初始偏移距离（像素）
    distance: {
      type: Number,
      default: 100
    },
    // 动画方向：垂直（vertical）/水平（horizontal）
    direction: {
      type: String,
      default: 'vertical',
      validator: val => ['vertical', 'horizontal'].includes(val)
    },
    // 是否反向偏移（true=反向，false=正向）
    reverse: {
      type: Boolean,
      default: false
    },
    // 动画持续时间（秒）
    duration: {
      type: Number,
      default: 0.8
    },
    // 动画缓动函数（支持GSAP缓动字符串或自定义函数）
    ease: {
      type: [String, Function],
      default: 'power3.out'
    },
    // 初始透明度（仅当animateOpacity=true时生效）
    initialOpacity: {
      type: Number,
      default: 0,
      validator: val => val >= 0 && val <= 1 // 限制透明度在0-1之间
    },
    // 是否启用透明度过渡
    animateOpacity: {
      type: Boolean,
      default: true
    },
    // 初始缩放比例（最终会过渡到1）
    scale: {
      type: Number,
      default: 1
    },
    // 触发动画的视口阈值（元素进入视口的比例）
    threshold: {
      type: Number,
      default: 0.1,
      validator: val => val >= 0 && val <= 1
    },
    // 动画延迟时间（秒）
    delay: {
      type: Number,
      default: 0
    },
    // 自定义类名
    className: {
      type: String,
      default: ''
    }
  },
  emits: ['complete'], // 声明动画完成事件
  data() {
    return {
      tween: null,
    };
  },
  methods: {
    getScroller() {
      return document.querySelector('.layout-content') || undefined;
    },
    cleanupOwn() {
      const el = this.$refs.containerRef;
      if (this.tween) {
        if (this.tween.scrollTrigger) this.tween.scrollTrigger.kill();
        this.tween.kill();
        this.tween = null;
      }
      if (el) gsap.killTweensOf(el);
    },
    initAnimation() {
      const el = this.$refs.containerRef;
      if (!el) return;

      this.cleanupOwn();

      const axis = this.direction === 'horizontal' ? 'x' : 'y';
      const offset = this.reverse ? -this.distance : this.distance;
      const startPct = (1 - this.threshold) * 100;
      const scroller = this.getScroller();

      gsap.set(el, {
        [axis]: offset,
        scale: this.scale,
        opacity: this.animateOpacity ? this.initialOpacity : 1
      });

      this.tween = gsap.to(el, {
        [axis]: 0,
        scale: 1,
        opacity: 1,
        duration: this.duration,
        ease: this.ease,
        delay: this.delay,
        onComplete: () => this.$emit('complete'),
        scrollTrigger: {
          trigger: el,
          scroller,
          start: `top ${startPct}%`,
          toggleActions: 'play none none none',
          once: true
        }
      });
      ScrollTrigger.refresh();
    }
  },
  // 组件挂载后初始化动画
  mounted() {
    this.initAnimation();
  },
  // 监听props变化，实时更新动画
  watch: {
    // 监听所有关键props，任意变化时重新初始化动画
    distance: 'initAnimation',
    direction: 'initAnimation',
    reverse: 'initAnimation',
    duration: 'initAnimation',
    ease: 'initAnimation',
    initialOpacity: 'initAnimation',
    animateOpacity: 'initAnimation',
    scale: 'initAnimation',
    threshold: 'initAnimation',
    delay: 'initAnimation',
    // 处理对象/函数类型的props（如ease为自定义函数时），需要深度监听
    ease: {
      handler: 'initAnimation',
      deep: true
    }
  },
  // 组件销毁前清理动画资源，避免内存泄漏
  beforeDestroy() {
    this.cleanupOwn();
  }
};
</script>

<style scoped>
/* GSAP will handle all transforms and opacity */
</style>